import { useEffect, useMemo, useState } from 'react';
import { Pressable } from 'react-native';
import { ScrollView, Text, XStack, YStack } from 'tamagui';
import type { IconName } from '@kiips/ui';
import { AdaptivePanel, IconButton, Input, useThemeColors, Icon } from '@kiips/ui';
import type {
  CostLineItem,
  Trip,
  TripCategory,
  TripParticipant,
} from '@my-fullstack-app/itinerary-types';
import { type CostBearing, resolveItemSplitWith } from '../../lib/cost';
import { usePreferences } from '../../hooks/usePreferences';
import { useAuth } from '../../contexts/AuthContext';
import { useItineraryStore } from '../../store/itinerary-store';
import { getParticipantDisplayName } from '../../lib/participant-utils';
import { CurrencyPicker } from '../design/CurrencyPicker';
import { ParticipantAvatar } from '../trip/participants/ParticipantAvatar';
import { getCategoryColor } from '../trip/categories/useCategoryColor';

/**
 * Per-line-item cost editor — ADR-0069. Replaces `CostFieldRow` +
 * `CostAttributionPills`: every item is one row with its own
 * amount / currency / label and a pill stack for inline
 * attribution. A trailing "+ Add cost" row appends a new item.
 *
 * The editor is composed and stateless beyond the input drafts.
 * All writes go through `addCostItem` / `updateCostItem` /
 * `removeCostItem` on the cost-items slice.
 */
interface Props {
  trip: Trip;
  bearer: CostBearing;
  canEdit: boolean;
}

const CATEGORY_HINT_BY_KIND: Record<CostBearing['kind'], string | undefined> = {
  place: 'food',
  custom: undefined,
  journey: 'transport',
  stay: 'lodging',
};

function suggestCategoryId(
  kind: CostBearing['kind'],
  categories: TripCategory[],
): string | undefined {
  const hint = CATEGORY_HINT_BY_KIND[kind];
  if (!hint) return undefined;
  return categories.find((c) => c.name.toLowerCase() === hint)?.id;
}

export function CostItemsEditor({ trip, bearer, canEdit }: Props) {
  const theme = useThemeColors();
  const items = useMemo(() => bearerItems(bearer), [bearer]);
  const addCostItem = useItineraryStore((s) => s.addCostItem);
  const removeCostItem = useItineraryStore((s) => s.removeCostItem);
  const { preferredCurrency } = usePreferences();
  const { user } = useAuth();
  const participants = useMemo(() => trip.participants ?? [], [trip.participants]);
  const categories = useMemo(() => trip.categories ?? [], [trip.categories]);
  // Default payer for new line items: the "me" participant matched by
  // userId from auth context. An authenticated user with no matching
  // participant (typical for an editor share where the user isn't in
  // participants[]) gets no default — they have to pick a payer
  // explicitly. Silently falling back to participants[0] would
  // misattribute their spend to whoever happens to sit first
  // (usually the trip owner). For non-authenticated callers (tests,
  // anonymous trips) the first participant is still the natural seed.
  const defaultPaidBy = useMemo(() => {
    if (user?.id) {
      return participants.find((p) => p.userId === user.id)?.id;
    }
    return participants[0]?.id;
  }, [participants, user?.id]);

  if (!canEdit && items.length === 0) return null;

  const handleAdd = () => {
    addCostItem(trip.id, bearer, {
      amount: 0,
      currency: preferredCurrency,
      ...(defaultPaidBy ? { paidBy: defaultPaidBy } : {}),
      ...(suggestCategoryId(bearer.kind, categories)
        ? { categoryId: suggestCategoryId(bearer.kind, categories) }
        : {}),
    });
  };

  return (
    <YStack gap={4}>
      {items.map((item) => (
        <CostItemRow
          key={item.id}
          trip={trip}
          bearer={bearer}
          item={item}
          canEdit={canEdit}
          singleItem={items.length === 1}
          onRemove={() => removeCostItem(trip.id, bearer, item.id)}
        />
      ))}
      {canEdit ? (
        <Pressable
          onPress={handleAdd}
          accessibilityRole="button"
          accessibilityLabel="Add cost"
          style={({ pressed }) => [
            {
              flexDirection: 'row' as const,
              alignItems: 'center' as const,
              gap: 10,
              paddingHorizontal: 12,
              paddingVertical: 10,
              borderRadius: 12,
              opacity: pressed ? 0.7 : 1,
            },
          ]}
        >
          <YStack
            width={28}
            height={28}
            borderRadius={10}
            backgroundColor="$surfaceCanvas"
            alignItems="center"
            justifyContent="center"
          >
            <Icon name="add" size={16} color={theme.textSubdued} />
          </YStack>
          <Text fontSize={13} fontWeight="600" style={{ color: theme.textSubdued }}>
            {items.length === 0 ? 'Add cost' : 'Add another'}
          </Text>
        </Pressable>
      ) : null}
    </YStack>
  );
}

function bearerItems(bearer: CostBearing): CostLineItem[] {
  switch (bearer.kind) {
    case 'place':
      return bearer.entity.costs ?? [];
    case 'custom':
      return bearer.entity.costs ?? [];
    case 'journey':
      return bearer.entity.booking?.costs ?? [];
    case 'stay':
      return bearer.entity.booking?.costs ?? [];
  }
}

type PickerMode = 'paidBy' | 'splitWith' | 'category';

interface RowProps {
  trip: Trip;
  bearer: CostBearing;
  item: CostLineItem;
  canEdit: boolean;
  singleItem: boolean;
  onRemove: () => void;
}

function CostItemRow({ trip, bearer, item, canEdit, singleItem, onRemove }: RowProps) {
  const theme = useThemeColors();
  const updateCostItem = useItineraryStore((s) => s.updateCostItem);
  const { preferredCurrency } = usePreferences();
  const participants = useMemo(() => trip.participants ?? [], [trip.participants]);
  const categories = useMemo(() => trip.categories ?? [], [trip.categories]);
  const [picker, setPicker] = useState<PickerMode | null>(null);

  // Local drafts keep the inputs responsive without flushing on
  // every keystroke. Sync back when the underlying item changes
  // (cross-device sync, undo).
  const [labelDraft, setLabelDraft] = useState(item.label ?? '');
  const [amountDraft, setAmountDraft] = useState(item.amount > 0 ? String(item.amount) : '');

  useEffect(() => {
    setLabelDraft(item.label ?? '');
    setAmountDraft(item.amount > 0 ? String(item.amount) : '');
  }, [item.label, item.amount]);

  const persistAmount = () => {
    const trimmed = amountDraft.trim();
    if (trimmed === '') {
      if (item.amount !== 0) updateCostItem(trip.id, bearer, item.id, { amount: 0 });
      return;
    }
    const num = Number(trimmed);
    if (!Number.isFinite(num) || num < 0) {
      setAmountDraft(item.amount > 0 ? String(item.amount) : '');
      return;
    }
    if (num !== item.amount) updateCostItem(trip.id, bearer, item.id, { amount: num });
  };

  const persistLabel = () => {
    const next = labelDraft.trim();
    if (next === (item.label ?? '')) return;
    updateCostItem(trip.id, bearer, item.id, { label: next || null });
  };

  const payer = item.paidBy ? participants.find((p) => p.id === item.paidBy) : undefined;
  const category = item.categoryId ? categories.find((c) => c.id === item.categoryId) : undefined;
  const splitIds = resolveItemSplitWith(trip, item);
  const splitLabel =
    participants.length === 0
      ? '—'
      : splitIds.length === participants.length
        ? 'Everyone'
        : splitIds.length === 0
          ? 'Just payer'
          : splitIds.length === 1
            ? (() => {
                const found = participants.find((p) => p.id === splitIds[0]);
                return found ? getParticipantDisplayName(found) : '1 person';
              })()
            : `${splitIds.length} people`;

  return (
    <YStack
      borderRadius={14}
      padding={10}
      gap={6}
      backgroundColor={singleItem ? undefined : '$surfaceCanvas'}
    >
      <XStack alignItems="center" gap={10}>
        <YStack
          width={32}
          height={32}
          borderRadius={10}
          backgroundColor={singleItem ? '$surfaceCanvas' : 'transparent'}
          alignItems="center"
          justifyContent="center"
        >
          <Icon name="payments" size={16} color={theme.textOnSurface} />
        </YStack>
        <Input
          variant="inline"
          value={labelDraft}
          onChangeText={setLabelDraft}
          editable={canEdit}
          onBlur={persistLabel}
          placeholder={singleItem ? 'Cost' : 'Label'}
          accessibilityLabel="Cost label"
          flex={1}
          fontSize={14}
          fontWeight="600"
        />
        <Input
          variant="inline"
          value={amountDraft}
          onChangeText={setAmountDraft}
          editable={canEdit}
          onBlur={persistAmount}
          keyboardType="decimal-pad"
          placeholder="0"
          accessibilityLabel="Cost amount"
          width={80}
          fontSize={15}
          fontWeight="700"
          textAlign="right"
        />
        <CurrencyPicker
          variant="inline"
          value={item.currency || preferredCurrency}
          onChange={(code) => {
            if (code !== item.currency) updateCostItem(trip.id, bearer, item.id, { currency: code });
          }}
          disabled={!canEdit}
        />
        {canEdit && !singleItem ? (
          <IconButton
            iconName="close"
            onPress={onRemove}
            accessibilityLabel="Remove cost item"
            size="sm"
            tone="subdued"
          />
        ) : null}
      </XStack>

      {/* Attribution pills. paidBy + splitWith are participant-bound,
          so they hide entirely when the trip has no participants —
          rendering them disabled would imply attribution is set up
          but broken. Category pill always renders (it doesn't depend
          on participants). Readers with no attribution at all see
          nothing. */}
      {canEdit || payer || category ? (
        <XStack gap={6} flexWrap="wrap" paddingLeft={42}>
          {participants.length > 0 ? (
            <>
              <AttributionPill
                icon="account-circle"
                avatarParticipant={payer}
                label={payer ? getParticipantDisplayName(payer) : 'Set payer'}
                ghost={!payer}
                disabled={!canEdit}
                onPress={() => setPicker('paidBy')}
              />
              <AttributionPill
                icon="group"
                label={`Split: ${splitLabel}`}
                ghost={!payer}
                disabled={!canEdit}
                onPress={() => setPicker('splitWith')}
              />
            </>
          ) : null}
          {categories.length > 0 ? (
            <AttributionPill
              icon="local-offer"
              dotColor={category ? getCategoryColor(category) : undefined}
              label={category?.name ?? 'No category'}
              ghost={!category}
              disabled={!canEdit}
              onPress={() => setPicker('category')}
            />
          ) : null}
        </XStack>
      ) : null}

      <AdaptivePanel
        open={picker !== null}
        onOpenChange={(v) => { if (!v) setPicker(null); }}
        title={
          picker === 'paidBy'
            ? 'Paid by'
            : picker === 'splitWith'
              ? 'Split with'
              : 'Category'
        }
        snapPoints={[80]}
      >
        <YStack padding={16} paddingTop={4} gap={4}>
          {picker === 'paidBy' ? <ParticipantSingleList
              participants={participants}
              value={item.paidBy}
              onSelect={(id) => {
                updateCostItem(trip.id, bearer, item.id, { paidBy: id });
                setPicker(null);
              }}
            /> : null}
          {picker === 'splitWith' ? <ParticipantMultiList
              participants={participants}
              value={splitIds}
              onChange={(next) => {
                const allIds = participants.map((p) => p.id);
                const isAll =
                  next.length === allIds.length &&
                  allIds.every((id) => next.includes(id));
                updateCostItem(trip.id, bearer, item.id, {
                  // Canonical "split with all" clears splitWith (null)
                  // so the field stays absent and later participant
                  // additions auto-join.
                  splitWith: isAll ? null : next,
                });
              }}
            /> : null}
          {picker === 'category' ? <CategoryList
              categories={categories}
              value={item.categoryId}
              onSelect={(id) => {
                updateCostItem(trip.id, bearer, item.id, { categoryId: id });
                setPicker(null);
              }}
            /> : null}
        </YStack>
      </AdaptivePanel>
    </YStack>
  );
}

interface PillProps {
  icon: IconName;
  label: string;
  /** Category swatch — a solid colored dot. Categories still carry
   *  a colorIndex (participants don't, per ADR-0057 amendment). */
  dotColor?: string | undefined;
  /** Participant identity — renders the avatar tile when set, in
   *  place of either the dot or the icon. */
  avatarParticipant?: TripParticipant | undefined;
  ghost?: boolean | undefined;
  disabled?: boolean | undefined;
  onPress: () => void;
}

function AttributionPill({ icon, label, dotColor, avatarParticipant, ghost, disabled, onPress }: PillProps) {
  const theme = useThemeColors();
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ pressed }) => [
        {
          flexDirection: 'row' as const,
          alignItems: 'center' as const,
          gap: 6,
          paddingHorizontal: 10,
          paddingVertical: 6,
          borderRadius: 14,
          backgroundColor: theme.surfaceCanvas,
          opacity: disabled ? 0.5 : 1,
        },
        pressed && { opacity: 0.7 },
      ]}
    >
      {avatarParticipant ? (
        <ParticipantAvatar participant={avatarParticipant} size={16} />
      ) : dotColor ? (
        <YStack width={10} height={10} borderRadius={5} backgroundColor={dotColor} />
      ) : (
        <Icon name={icon} size={14} color={theme.textSubdued} />
      )}
      <Text
        fontSize={12}
        fontWeight="600"
        fontStyle={ghost ? 'italic' : 'normal'}
        style={{ color: ghost ? theme.textSubdued : theme.textOnSurface }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

interface ParticipantSingleListProps {
  participants: TripParticipant[];
  value: string | undefined;
  onSelect: (id: string) => void;
}

function ParticipantSingleList({ participants, value, onSelect }: ParticipantSingleListProps) {
  return (
    <ScrollView maxHeight={400}>
      <YStack gap={2}>
        {participants.map((p) => (
          <ParticipantRow
            key={p.id}
            participant={p}
            selected={p.id === value}
            onPress={() => onSelect(p.id)}
          />
        ))}
      </YStack>
    </ScrollView>
  );
}

interface ParticipantMultiListProps {
  participants: TripParticipant[];
  value: string[];
  onChange: (next: string[]) => void;
}

function ParticipantMultiList({ participants, value, onChange }: ParticipantMultiListProps) {
  const theme = useThemeColors();
  const selected = new Set(value);
  const allIds = participants.map((p) => p.id);
  const isAll = participants.every((p) => selected.has(p.id));
  const toggle = (id: string) => {
    if (selected.has(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };
  const toggleAll = () => {
    onChange(isAll ? [] : allIds);
  };
  return (
    <ScrollView maxHeight={400}>
      <YStack gap={2}>
        <Pressable
          onPress={toggleAll}
          accessibilityRole="button"
          accessibilityLabel={isAll ? 'Deselect everyone' : 'Select everyone'}
          style={({ pressed }) => [
            {
              flexDirection: 'row' as const,
              alignItems: 'center' as const,
              gap: 10,
              paddingHorizontal: 12,
              paddingVertical: 10,
              borderRadius: 10,
            },
            pressed && { opacity: 0.7 },
          ]}
        >
          <Icon
            name={isAll ? 'check-box' : 'check-box-outline-blank'}
            size={20}
            color={isAll ? theme.accentCerulean : theme.textSubdued}
          />
          <Text fontSize={14} fontWeight="700" style={{ color: theme.textOnSurface }}>
            Everyone
          </Text>
        </Pressable>
        {participants.map((p) => (
          <ParticipantRow
            key={p.id}
            participant={p}
            selected={selected.has(p.id)}
            multiSelect
            onPress={() => toggle(p.id)}
          />
        ))}
      </YStack>
    </ScrollView>
  );
}

interface ParticipantRowProps {
  participant: TripParticipant;
  selected: boolean;
  multiSelect?: boolean | undefined;
  onPress: () => void;
}

function ParticipantRow({ participant, selected, multiSelect, onPress }: ParticipantRowProps) {
  const theme = useThemeColors();
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      accessibilityLabel={getParticipantDisplayName(participant)}
      style={({ pressed }) => [
        {
          flexDirection: 'row' as const,
          alignItems: 'center' as const,
          gap: 10,
          paddingHorizontal: 12,
          paddingVertical: 10,
          borderRadius: 10,
        },
        pressed && { opacity: 0.7 },
      ]}
    >
      {multiSelect ? (
        <Icon
          name={selected ? 'check-box' : 'check-box-outline-blank'}
          size={20}
          color={selected ? theme.accentCerulean : theme.textSubdued}
        />
      ) : (
        <Icon
          name={selected ? 'radio-button-checked' : 'radio-button-unchecked'}
          size={20}
          color={selected ? theme.accentCerulean : theme.textSubdued}
        />
      )}
      <ParticipantAvatar participant={participant} size={22} />
      <Text flex={1} fontSize={14} fontWeight="600" style={{ color: theme.textOnSurface }}>
        {getParticipantDisplayName(participant)}
      </Text>
    </Pressable>
  );
}

interface CategoryListProps {
  categories: TripCategory[];
  value: string | undefined;
  onSelect: (id: string | undefined) => void;
}

function CategoryList({ categories, value, onSelect }: CategoryListProps) {
  const theme = useThemeColors();
  return (
    <ScrollView maxHeight={400}>
      <YStack gap={2}>
        <Pressable
          onPress={() => onSelect(undefined)}
          accessibilityRole="button"
          accessibilityState={{ selected: value === undefined }}
          accessibilityLabel="No category"
          style={({ pressed }) => [
            {
              flexDirection: 'row' as const,
              alignItems: 'center' as const,
              gap: 10,
              paddingHorizontal: 12,
              paddingVertical: 10,
              borderRadius: 10,
            },
            pressed && { opacity: 0.7 },
          ]}
        >
          <Icon
            name={value === undefined ? 'radio-button-checked' : 'radio-button-unchecked'}
            size={20}
            color={value === undefined ? theme.accentCerulean : theme.textSubdued}
          />
          <Text flex={1} fontSize={14} fontStyle="italic" style={{ color: theme.textSubdued }}>
            No category
          </Text>
        </Pressable>
        {categories.map((c) => (
          <Pressable
            key={c.id}
            onPress={() => onSelect(c.id)}
            accessibilityRole="button"
            accessibilityState={{ selected: c.id === value }}
            accessibilityLabel={c.name}
            style={({ pressed }) => [
              {
                flexDirection: 'row' as const,
                alignItems: 'center' as const,
                gap: 10,
                paddingHorizontal: 12,
                paddingVertical: 10,
                borderRadius: 10,
              },
              pressed && { opacity: 0.7 },
            ]}
          >
            <Icon
              name={c.id === value ? 'radio-button-checked' : 'radio-button-unchecked'}
              size={20}
              color={c.id === value ? theme.accentCerulean : theme.textSubdued}
            />
            <YStack
              width={14}
              height={14}
              borderRadius={7}
              backgroundColor={getCategoryColor(c)}
            />
            <Text flex={1} fontSize={14} fontWeight="600" style={{ color: theme.textOnSurface }}>
              {c.name}
            </Text>
          </Pressable>
        ))}
      </YStack>
    </ScrollView>
  );
}
